/**
 * Tracks the FactSet host context (PowerPoint vs. Excel) for the intent bridge.
 *
 * When the addin runs inside a FactSet-hosted iframe (bypass mode), we don't
 * have Office.js to tell us whether we're in PowerPoint or Excel. FactSet
 * surfaces that information via `factset.hostContext`, which can be obtained
 * two ways:
 *
 *  1. Cached from the `factset.onConnected` handshake (preferred — zero-cost
 *     if FactSet emits it unprompted).
 *  2. Explicitly via the `factset.GetHostContext` intent (authoritative).
 *
 * PowerPoint-only intents (`factset.Presentation.*`) fail with an
 * unsupported-intent error against a spreadsheet host, so PPT code paths must
 * consult this module before dispatching.
 */

import {
  isFactSetIntentBridgeAvailable,
  raiseIntent,
} from "./factsetIntentTransport";

export type FactSetAppType = "presentation" | "spreadsheet" | "unknown";
export type FactSetSpreadsheetNoteCapability =
  | "supported"
  | "unsupported"
  | "unknown";

export const MIN_FACTSET_NOTE_ATLAS_VERSION = "2016.73.179.020";

export interface FactSetHostContext {
  appType: FactSetAppType;
  platformType?: string;
  atlasVersion?: string;
  source: "onConnected" | "intent" | "cache";
}

const HOST_CONTEXT_INTENT_TIMEOUT_MS = 8_000;

let cachedHostContext: FactSetHostContext | null = null;
let inFlightEnsure: Promise<FactSetHostContext | null> | null = null;
let listenerInstalled = false;
const onResolveListeners = new Set<(ctx: FactSetHostContext) => void>();

type Logger = (message: string) => void;

async function raiseHostContextIntent(
  timeoutMs = HOST_CONTEXT_INTENT_TIMEOUT_MS
): Promise<{ appType?: string; platformType?: string; atlasVersion?: string }> {
  const raw = await raiseIntent("factset.GetHostContext", { type: "factset.hostContext" }, timeoutMs);
  if (!raw || typeof raw !== "object") {
    return {};
  }
  const obj = raw as Record<string, unknown>;
  const hostContextCandidate =
    (obj.hostContext && typeof obj.hostContext === "object"
      ? (obj.hostContext as Record<string, unknown>)
      : undefined) ??
    (obj.context && typeof obj.context === "object"
      ? ((obj.context as Record<string, unknown>).hostContext as Record<string, unknown> | undefined)
      : undefined);
  const source = hostContextCandidate ?? obj;
  const appType = typeof source.appType === "string" ? source.appType : undefined;
  const platformType = typeof source.platformType === "string" ? source.platformType : undefined;
  const atlasVersion =
    typeof source.atlasVersion === "string" ? source.atlasVersion : undefined;
  return { appType, platformType, atlasVersion };
}

function logMessage(log: Logger | undefined, message: string): void {
  if (log) {
    log(message);
  } else {
    console.log(message);
  }
}

function normalizeAppType(raw: unknown): FactSetAppType {
  if (typeof raw !== "string") return "unknown";
  const normalized = raw.trim().toLowerCase();
  if (normalized === "presentation") return "presentation";
  if (normalized === "spreadsheet") return "spreadsheet";
  return "unknown";
}

function normalizePlatformType(raw: unknown): string | undefined {
  if (typeof raw !== "string") return undefined;
  const trimmed = raw.trim();
  return trimmed.length > 0 ? trimmed : undefined;
}

function normalizeAtlasVersion(raw: unknown): string | undefined {
  if (typeof raw !== "string") return undefined;
  const trimmed = raw.trim();
  return trimmed.length > 0 ? trimmed : undefined;
}

function parseAtlasVersion(value: string): number[] | undefined {
  const parts = value.trim().split(".");
  if (parts.length < 2 || parts.length > 4) return undefined;
  const parsed = parts.map((part) => {
    if (!part || !Array.from(part).every((character) => character >= "0" && character <= "9")) {
      return Number.NaN;
    }
    return Number(part);
  });
  if (parsed.some((part) => !Number.isSafeInteger(part))) return undefined;
  while (parsed.length < 4) parsed.push(0);
  return parsed;
}

export function compareAtlasVersions(
  left: string,
  right: string,
): number | undefined {
  const leftParts = parseAtlasVersion(left);
  const rightParts = parseAtlasVersion(right);
  if (!leftParts || !rightParts) return undefined;
  for (let index = 0; index < 4; index += 1) {
    if (leftParts[index] < rightParts[index]) return -1;
    if (leftParts[index] > rightParts[index]) return 1;
  }
  return 0;
}

function extractHostContextFromOnConnected(
  payload: unknown
): {
  appType: FactSetAppType;
  platformType?: string;
  atlasVersion?: string;
} | null {
  if (!payload || typeof payload !== "object") return null;
  const hostContext = (payload as Record<string, unknown>).hostContext;
  if (!hostContext || typeof hostContext !== "object") return null;
  const obj = hostContext as Record<string, unknown>;
  const appType = normalizeAppType(obj.appType);
  if (appType === "unknown") return null;
  return {
    appType,
    platformType: normalizePlatformType(obj.platformType),
    atlasVersion: normalizeAtlasVersion(obj.atlasVersion),
  };
}

function recordHostContext(
  next: FactSetHostContext,
  log?: Logger
): FactSetHostContext {
  const previous = cachedHostContext;
  cachedHostContext = next;
  if (!previous || previous.appType !== next.appType) {
    logMessage(
      log,
      `Host context resolved: appType=${next.appType}` +
        (next.platformType ? `, platformType=${next.platformType}` : "") +
        (next.atlasVersion ? `, atlasVersion=${next.atlasVersion}` : "") +
        ` (source=${next.source})`
    );
  }
  onResolveListeners.forEach((listener) => {
    try {
      listener(next);
    } catch (error) {
      console.error("Host context listener threw:", error);
    }
  });
  return next;
}

function handleParentMessage(event: MessageEvent): void {
  if (typeof window === "undefined") return;
  if (event.source !== window.parent) return;
  const data = event.data;
  if (!data || typeof data !== "object") return;
  if ((data as Record<string, unknown>).eventName !== "factset.onConnected") {
    return;
  }
  const parsed = extractHostContextFromOnConnected(data);
  if (!parsed) return;
  recordHostContext({ ...parsed, source: "onConnected" });
}

/**
 * Idempotently install the `factset.onConnected` listener so we can capture
 * host context opportunistically without needing to raise a dedicated intent.
 * Safe to call from module load and from top-level boot code.
 */
export function initFactSetHostContextListener(): void {
  if (listenerInstalled) return;
  if (typeof window === "undefined") return;
  if (!window.parent || window.parent === window) return;
  window.addEventListener("message", handleParentMessage);
  listenerInstalled = true;
}

if (typeof window !== "undefined") {
  initFactSetHostContextListener();
}

export function getCachedFactSetHostContext(): FactSetHostContext | null {
  return cachedHostContext;
}

export function isFactSetPowerPointHost(): boolean {
  return cachedHostContext?.appType === "presentation";
}

export function isFactSetSpreadsheetHost(): boolean {
  return cachedHostContext?.appType === "spreadsheet";
}

export function getFactSetSpreadsheetNoteCapability(
  context: FactSetHostContext | null = cachedHostContext,
): FactSetSpreadsheetNoteCapability {
  if (context?.appType && context.appType !== "spreadsheet") {
    return "unsupported";
  }
  if (!context?.atlasVersion) return "unknown";
  const comparison = compareAtlasVersions(
    context.atlasVersion,
    MIN_FACTSET_NOTE_ATLAS_VERSION,
  );
  if (comparison === undefined) return "unknown";
  return comparison >= 0 ? "supported" : "unsupported";
}

/**
 * Register a callback that fires whenever the host context is (re)resolved.
 * Returns an unsubscribe function.
 */
export function onFactSetHostContextResolved(
  listener: (ctx: FactSetHostContext) => void
): () => void {
  onResolveListeners.add(listener);
  return () => {
    onResolveListeners.delete(listener);
  };
}

/**
 * Reset internal state. Exposed for tests and reconnect flows only.
 */
export function resetFactSetHostContextForTesting(): void {
  cachedHostContext = null;
  inFlightEnsure = null;
}

/**
 * Returns the host context, fetching it via `factset.GetHostContext` if it was
 * not already captured from `factset.onConnected`. Resolves to `null` if the
 * intent bridge is unreachable or the parent failed to respond within the
 * timeout.
 */
export async function ensureFactSetHostContext(
  options?: { log?: Logger; timeoutMs?: number; forceRefresh?: boolean }
): Promise<FactSetHostContext | null> {
  const log = options?.log;
  const forceRefresh = options?.forceRefresh ?? false;

  if (!forceRefresh && cachedHostContext) {
    return cachedHostContext;
  }
  if (inFlightEnsure) {
    return inFlightEnsure;
  }
  if (!isFactSetIntentBridgeAvailable()) {
    return null;
  }

  const timeoutMs = options?.timeoutMs ?? HOST_CONTEXT_INTENT_TIMEOUT_MS;
  const fetchPromise = (async () => {
    try {
      const result = await raiseHostContextIntent(timeoutMs);
      const appType = normalizeAppType(result.appType);
      if (appType === "unknown") {
        logMessage(
          log,
          `factset.GetHostContext returned an unrecognized appType: ${String(result.appType)}`
        );
        return cachedHostContext;
      }
      return recordHostContext({
        appType,
        platformType: normalizePlatformType(result.platformType),
        atlasVersion: normalizeAtlasVersion(result.atlasVersion),
        source: "intent",
      });
    } catch (error) {
      const message = error instanceof Error ? error.message : String(error);
      logMessage(log, `factset.GetHostContext failed: ${message}`);
      return cachedHostContext;
    }
  })();

  inFlightEnsure = fetchPromise.finally(() => {
    inFlightEnsure = null;
  });
  return inFlightEnsure;
}
